import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Circle, Popup, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ArrowLeft, MapPin, Search, ShieldCheck, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ProPortalHeader from '@/components/ProPortalHeader';
import { formatApiError } from '@/utils/apiError';
import { PostcodeNudgeButton } from '@/components/staff/PostcodeNudgeButton';

const BRANDS = [
  { id: 'probound', label: 'ProBound', color: '#2F5E96' },
  { id: 'proepoxy', label: 'ProEpoxy', color: '#7A0B7A' },
  { id: 'prodecor', label: 'ProDecor', color: '#B8860B' },
];
const UK_CENTRE = [54.2, -2.5];
const pinIcon = L.divIcon({ className: '', html: '<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;background:#dc2626;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>', iconSize: [18, 18], iconAnchor: [9, 18] });

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo([target.lat, target.lng], 9, { duration: 0.8 }); }, [target, map]);
  return null;
}

export default function StaffInstallerMap() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [brand, setBrand] = useState('all');
  const [approvedOnly, setApprovedOnly] = useState(false);
  const [withinOnly, setWithinOnly] = useState(false);
  const [showRadius, setShowRadius] = useState(false);
  const [postcode, setPostcode] = useState('');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async (pc = query) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/installer-map${pc ? `?postcode=${encodeURIComponent(pc)}` : ''}`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d) || 'Failed to load');
      setData(d);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  useEffect(() => { load(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const geocodeMissing = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/installer-map/geocode-missing`, { method: 'POST', credentials: 'include' });
      const d = await r.json();
      toast.success(`Located ${d.geocoded} installer${d.geocoded === 1 ? '' : 's'}${d.failed ? ` · ${d.failed} postcodes not found` : ''}`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const visible = useMemo(() => (data?.installers || []).filter((i) => {
    if (brand !== 'all' && !i.brands.includes(brand)) return false;
    if (approvedOnly && !(brand === 'all' ? i.approved.length > 0 : i.approved.includes(brand))) return false;
    if (withinOnly && data?.target && i.within_radius === false) return false;
    return true;
  }), [data, brand, approvedOnly, withinOnly]);

  const colourFor = (i) => {
    const b = brand !== 'all' ? brand : i.brands[0];
    return BRANDS.find((x) => x.id === b)?.color || '#6B7280';
  };
  const isApproved = (i) => (brand === 'all' ? i.approved.length > 0 : i.approved.includes(brand));

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      <ProPortalHeader activeSection="staff" />
      <main className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4">
        <button onClick={() => navigate('/staff')} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-2" data-testid="map-back"><ArrowLeft className="w-4 h-4" /> Staff Dashboard</button>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#27292E] flex items-center gap-2"><MapPin className="w-7 h-7 text-[#006400]" /> Installer Map</h1>
            <p className="text-sm text-slate-600">Where your installers are — filter by sector and Ox Approved status, or find the nearest to a customer's postcode.</p>
          </div>
          {data && (
            <div className="text-xs text-slate-600 flex flex-wrap items-center gap-2 sm:gap-3" data-testid="map-counts">
              <span><strong>{visible.length}</strong> shown · <strong>{data.counts.mapped}</strong> located</span>
              {data.counts.no_postcode > 0 && <span title="These installers haven't added a postcode to their Business Profile yet" data-testid="map-no-postcode">{data.counts.no_postcode} without a postcode</span>}
              {data.counts.ungeocoded > 0 && <Button size="sm" variant="outline" onClick={geocodeMissing} disabled={busy} data-testid="map-geocode-missing"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Locate {data.counts.ungeocoded} with postcode</Button>}
              <PostcodeNudgeButton />
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="inline-flex flex-wrap bg-white border rounded-lg p-0.5" data-testid="map-brand-filter">
            {[{ id: 'all', label: 'All sectors', color: '#27292E' }, ...BRANDS].map((b) => (
              <button key={b.id} onClick={() => setBrand(b.id)} data-testid={`map-brand-${b.id}`} className="px-3 py-1.5 text-sm font-semibold rounded-md transition-colors" style={brand === b.id ? { background: b.color, color: '#fff' } : { color: '#4b5563' }}>{b.label}</button>
            ))}
          </div>
          <button onClick={() => setApprovedOnly((v) => !v)} data-testid="map-approved-toggle" className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border-2 ${approvedOnly ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-gray-200 text-gray-700'}`}>
            <ShieldCheck className="w-4 h-4" /> Ox Approved only
          </button>
          <button onClick={() => setShowRadius((v) => !v)} data-testid="map-radius-toggle" className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border-2 ${showRadius ? 'bg-[#27292E] border-[#27292E] text-white' : 'bg-white border-gray-200 text-gray-700'}`}>Show travel radius</button>
          {data?.target && (
            <button onClick={() => setWithinOnly((v) => !v)} data-testid="map-within-toggle" className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border-2 ${withinOnly ? 'bg-[#006400] border-[#006400] text-white' : 'bg-white border-gray-200 text-gray-700'}`}>Would travel here only</button>
          )}
          <form onSubmit={(e) => { e.preventDefault(); setQuery(postcode.trim()); load(postcode.trim()); }} className="flex gap-1.5 ml-auto">
            <Input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Customer postcode, e.g. M1 1AA" className="h-10 w-56 bg-white" data-testid="map-postcode" />
            <Button type="submit" className="h-10 bg-[#27292E] text-white" disabled={busy} data-testid="map-search"><Search className="w-4 h-4" /></Button>
            {query && <Button type="button" variant="outline" className="h-10" onClick={() => { setPostcode(''); setQuery(''); load(''); }} data-testid="map-clear"><X className="w-4 h-4" /></Button>}
          </form>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-3">
          <div className="rounded-xl overflow-hidden border bg-white shadow-sm h-[70vh]" data-testid="map-container">
            <MapContainer center={UK_CENTRE} zoom={6} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FlyTo target={data?.target} />
              {data?.target && <Marker position={[data.target.lat, data.target.lng]} icon={pinIcon}><Popup>Customer · {data.target.postcode}</Popup></Marker>}
              {showRadius && visible.map((i) => (
                <Circle key={`r-${i.user_id}`} center={[i.lat, i.lng]} radius={(i.coverage_radius_miles || 30) * 1609.34} pathOptions={{ color: colourFor(i), weight: 1, fillOpacity: 0.05, dashArray: '4 4' }} />
              ))}
              {visible.map((i) => (
                <CircleMarker key={i.user_id} center={[i.lat, i.lng]} radius={isApproved(i) ? 9 : 7}
                  pathOptions={{ color: isApproved(i) ? '#059669' : '#fff', weight: isApproved(i) ? 3 : 1.5, fillColor: colourFor(i), fillOpacity: 0.9 }}
                  eventHandlers={{ click: () => setSelected(i.user_id) }}>
                  <Popup>
                    <div className="text-sm min-w-[180px]">
                      <div className="font-bold">{i.company_name || i.name}</div>
                      <div className="text-gray-500 text-xs">{i.name}{i.town ? ` · ${i.town}` : ''}{i.postcode ? ` · ${i.postcode}` : ''}</div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {i.brands.map((b) => <span key={b} className="text-[10px] font-bold text-white rounded px-1.5 py-0.5" style={{ background: BRANDS.find((x) => x.id === b).color }}>{BRANDS.find((x) => x.id === b).label}{i.approved.includes(b) ? ' ✓' : ''}</span>)}
                      </div>
                      <div className="text-xs mt-1 text-gray-600">Travels up to {i.coverage_radius_miles || 30} miles{!i.coverage_radius_miles ? ' (default)' : ''}</div>
                      {i.distance_miles != null && <div className={`text-xs mt-1 font-semibold ${i.within_radius ? 'text-emerald-700' : 'text-red-600'}`}>{i.distance_miles} miles away{i.within_radius ? '' : ' — outside their radius'}</div>}
                      <button onClick={() => navigate(`/staff/installers/${i.user_id}`)} className="mt-2 text-xs font-semibold text-[#006400] underline" data-testid={`map-popup-profile-${i.user_id}`}>Open profile →</button>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>

          <aside className="bg-white border rounded-xl shadow-sm h-[70vh] overflow-y-auto" data-testid="map-list">
            <div className="px-3 py-2 border-b text-xs font-bold uppercase tracking-wider text-gray-500 sticky top-0 bg-white">{query ? `Nearest to ${data?.target?.postcode || query}` : 'Installers'} ({visible.length})</div>
            {visible.length === 0 && <p className="p-4 text-sm text-gray-500" data-testid="map-list-empty">No installers match — {data?.counts.mapped === 0 ? 'no installers have a postcode on their Business Profile yet.' : 'try another sector or turn off the Approved filter.'}</p>}
            {visible.map((i) => (
              <button key={i.user_id} onClick={() => { setSelected(i.user_id); navigate(`/staff/installers/${i.user_id}`); }} className={`w-full text-left px-3 py-2.5 border-b hover:bg-gray-50 ${selected === i.user_id ? 'bg-[#006400]/5' : ''}`} data-testid={`map-row-${i.user_id}`}>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: colourFor(i), boxShadow: isApproved(i) ? '0 0 0 2px #059669' : 'none' }} />
                  <span className="text-sm font-semibold text-gray-900 truncate flex-1">{i.company_name || i.name}</span>
                  {i.distance_miles != null && <span className={`text-xs font-bold ${i.within_radius ? 'text-gray-700' : 'text-red-500'}`}>{i.distance_miles} mi</span>}
                </div>
                {i.featured && <span className="ml-5 inline-block text-[10px] font-bold bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 mb-0.5" data-testid={`map-featured-${i.user_id}`}>★ {i.plan} — featured</span>}
                <div className="text-xs text-gray-500 ml-5 truncate">{i.town || i.postcode || '—'} · {i.brands.map((b) => BRANDS.find((x) => x.id === b).label).join(', ') || 'no sectors'}{i.approved.length ? ` · ✓ Approved ${i.approved.map((b) => BRANDS.find((x) => x.id === b).label).join('/')}` : ''}</div>
              </button>
            ))}
          </aside>
        </div>
        <p className="text-[11px] text-gray-500 mt-2">Coloured dot = sector · green ring = Ox Approved in that sector · red pin = searched postcode. Pins come from the postcode on each installer's Business Profile.</p>
      </main>
    </div>
  );
}
